import React, { useEffect, useRef, useState } from "react";
import { RootState, useAppDispatch, useAppSelector } from "../../../store";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../helpers/permissionModule";
import FullScreenLoader from "../../Components/FullScreenLoader";
import NoPermissionPage from "../../Components/NoPermissionPage";
import { svg } from "../../../Assets/svg";
import {
  formatToLocalDate,
  getAntdSortOrder,
  useDebounce,
} from "../../../helpers/utils";
import { Dropdown, Menu, Spin, Table, TableColumnsType } from "antd";
import NotificationButton from "../../Components/NotificationButton";
import CreateProductTagModal from "./Components/Modals/CreateProductTagModal";
import NoDataMessage from "../../Components/NoData";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  deleteProductTagAPI,
  getProductTagListAPI,
  reorderProductTagAPI,
} from "./productTagApis";
import { getDecryptedName } from "../../../helpers/bycrypt";
import { CSS } from "@dnd-kit/utilities";
import { CommonConfirmationModal } from "../../../Components/CommonConfirmationModal";

const DragHandle = (props: any) => {
  const { setActivatorNodeRef, listeners } = useSortable({ id: props.id });
  return (
    <div
      ref={setActivatorNodeRef}
      {...listeners}
      className="cursor-grab p-2 svg-24"
      style={{
        touchAction: "none",
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        userSelect: "none",
      }}
    >
      {svg?.dragHandleDots || "⋮⋮"}
    </div>
  );
};

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  "data-row-key": string;
}

const Row = (props: RowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props["data-row-key"],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 })?.replace(
      /translate3d\(([^,]+),([^,]+),([^)]+)\)/,
      'translate3d($1, $2, 0)',
    ),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  };

  return (
    <tr
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`${props.className || ""} ${
        isDragging ? "bg-light opacity-75 shadow-sm" : ""
      }`}
    />
  );
};

const ProductTags = () => {
  const dispatch = useAppDispatch();

  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData?.MASTER_DATA,
  );

  const abortControllerRef = useRef<AbortController | null>(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 991.98);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 991.98);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit] = useState<number>(30);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState<boolean>(false);

  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 300);

  const { authLoading } = useAppSelector((state: RootState) => state?.loading);
  const [reordering, setReordering] = useState<boolean>(false);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [reorderItemsList, setReorderItemsList] = useState<any[]>([]);
  const [productTags, setProductTags] = useState<any[]>([]);

  const [selectedProductTag, setSelectedProductTag] = useState<any>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [idToDelete, setIdToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [sorterState, setSorterState] = useState<{
    field: string | null;
    order: "asc" | "desc" | null;
  }>({ field: null, order: null });

  const fetchProductTags = (page: number, isReset: boolean) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (isReset) {
      setLoading(true);
    }

    const filters = {
      page: page,
      limit: limit,
      search: debouncedSearch,
      sortBy: sorterState.field,
      sortOrder: sorterState.order,
    };

    dispatch(
      getProductTagListAPI({
        filters,
        signal: controller.signal,
        onSuccess: (data: any) => {
          const newData = data?.productTags || [];
          const total = data?.total || 0;
          setTotalCount(total);

          if (isReset) {
            setProductTags(newData);
            window.scrollTo(0, 0);
          } else {
            setProductTags((prev) => [...prev, ...newData]);
          }

          const currentCount = isReset
            ? newData.length
            : productTags.length + newData.length;
          setHasMore(currentCount < total);
          setLoading(false);
        },
        onError: () => {
          setLoading(false);
        },
      }),
    );
  };

  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    fetchProductTags(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, sorterState]);

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchProductTags(nextPage, false);
  };

  // --- MENUS ---
  const headerMoreMenu = (
    <Menu>
      {USER_PERMISSIONS?.MODIFY && (
        <Menu.Item
          key="1"
          onClick={() => {
            setSearch("");
            setSorterState({ field: null, order: null });
            setIsReorderMode(true);
          }}
        >
          Reorder
        </Menu.Item>
      )}
    </Menu>
  );

  const tableComponents = React.useMemo(() => {
    return isReorderMode
      ? {
          body: {
            row: Row,
          },
        }
      : undefined;
  }, [isReorderMode]);

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const order =
      sorter.order === "ascend"
        ? "asc"
        : sorter.order === "descend"
        ? "desc"
        : null;
    setSorterState({ field: sorter.field, order: order as any });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
  );

  const handleSaveOrder = (
    draggedId: string,
    oldIndex: number,
    newIndex: number,
  ) => {
    setReordering(true);
    const payload = { id: draggedId, order: newIndex };
    dispatch(
      reorderProductTagAPI({
        payload,
        onSuccess: () => {
          setReordering(false);
        },
        onError: () => {
          setReordering(false);
          fetchProductTags(1, true); // Revert on error
        },
      }),
    );
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    const activeIndex = productTags?.findIndex((i) => i._id === active.id);
    const overIndex = productTags?.findIndex((i) => i._id === over.id);

    if (activeIndex === -1 || overIndex === -1) {
      return;
    }

    const reordered = arrayMove(productTags, activeIndex, overIndex);
    setProductTags(reordered);

    handleSaveOrder(active.id as string, activeIndex, overIndex);
  };

  const handleEditClick = (record: any) => {
    setSelectedProductTag(record);
    setIsCreateModalOpen(true);
  };

  const MoreOptionsMenu = (data: any) => {
    return (
      <Menu>
        {USER_PERMISSIONS.MODIFY && (
          <Menu.Item key="1" onClick={() => handleEditClick(data)}>
            <div className="text-black d-flex align-items-center gap-2 svg-20">
              Modify
            </div>
          </Menu.Item>
        )}

        {/* ✅ Remove Guard */}
        {USER_PERMISSIONS.REMOVE && !data?.is_default && (
          <Menu.Item key="2" onClick={() => setIdToDelete(data._id)}>
            <div className="text-danger d-flex align-items-center gap-2 svg-20">
              Remove
            </div>
          </Menu.Item>
        )}
      </Menu>
    );
  };

  const columns: TableColumnsType<any> = [
    ...(isReorderMode
      ? [
          {
            title: "",
            key: "sort",
            width: "1%",
            render: (_: any, record: any) => <DragHandle id={record._id} />,
          },
        ]
      : []),
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: isReorderMode ? false : true,
      sortOrder: getAntdSortOrder("name", sorterState),
      render: (name: string, record: any) => (
        <div className="d-flex align-items-center">
          <span
            style={{
              backgroundColor: record?.color ?? "#000000",
            }}
            className="px-2 py-1 rounded-3 text-white"
          >
            {name}
          </span>
        </div>
      ),
    },
    {
      title: "Created By",
      dataIndex: "created_by",
      key: "created_by",
      sorter: isReorderMode ? false : true,
      sortOrder: getAntdSortOrder("created_by", sorterState),
      render: (_, record) => {
        // Use helper to decrypt nested user name
        const fullName = getDecryptedName(record.created_by) || "-";
        return <span>{fullName}</span>;
      },
    },
    {
      title: "Created On",
      dataIndex: "created_at", // Mapped to backend key
      key: "created_at",
      sorter: isReorderMode ? false : true,
      sortOrder: getAntdSortOrder("created_at", sorterState),
      render: (date: string) => formatToLocalDate(date, true), // Format date
    },
    ...(!isReorderMode && (USER_PERMISSIONS?.MODIFY || USER_PERMISSIONS?.REMOVE)
      ? [
          {
            title: "Action",
            key: "action",
            render: (_: unknown, record: any) => (
              <div
                className="d-flex justify-content-center"
                onClick={(e) => e.stopPropagation()}
              >
                <Dropdown
                  overlay={() => MoreOptionsMenu(record)}
                  trigger={["click"]}
                  placement="bottomRight"
                  className="cursor-pointer"
                  overlayClassName="moreOptionDropdown"
                >
                  <span className="svg-32 svg-outline-primary" role="button">
                    {svg?.tableMoreOption}
                  </span>
                </Dropdown>
              </div>
            ),
            width: 10,
          },
        ]
      : []),
  ];

  const handleDeleteConfirm = () => {
    if (!idToDelete) return;

    setDeleting(true);

    dispatch(
      deleteProductTagAPI({
        id: idToDelete,
        onSuccess: () => {
          setDeleting(false);
          setIdToDelete(null);
          setCurrentPage(1);
          fetchProductTags(1, true);
        },
        onError: () => {
          setDeleting(false);
        },
      }),
    );
  };

  const handleCloseDeleteModal = () => {
    setIdToDelete(null);
  };

  return USER_PERMISSIONS?.READ ? (
    <>
      <div className="page-content h-100 px-40 py-40 d-flex flex-column">
        <div className="flex-shrink-0">
          {isReorderMode ? (
            <div className="mb-sm-4 mb-3 gap-2 d-flex flex-wrap justify-content-sm-between justify-content-center align-items-center">
              <div className="d-flex align-items-center gap-sm-3 gap-2 mob-w-100">
                <span
                  className="cursor-pointer svg-24"
                  onClick={() => {
                    setIsReorderMode(false);
                    setReorderItemsList([]);
                  }}
                >
                  {svg?.backArrow || "←"}
                </span>
                <h2 className="text-black fs-36 fw-bolder page-title mb-0">
                  Reorder Tags
                </h2>
              </div>
              <div className="row mob-w-100">
                <div className="col-sm-auto col-6">
                  <button
                    className="btn btn-outline-secondary px-4 fw-bold w-100"
                    onClick={() => {
                      setIsReorderMode(false);
                      setReorderItemsList([]);
                    }}
                    disabled={reordering}
                  >
                    CLOSE
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-sm-4 mb-3 d-flex flex-lg-nowrap flex-wrap gap-md-3 gap-2 justify-content-between align-items-center">
              <h2 className="text-black fs-36 fw-bolder page-title mb-0 order-sm-1 order-1">
                Product Tags {totalCount > 0 && `(${totalCount})`}
              </h2>
              <div className="pageSearch mw-100 position-relative ms-sm-auto order-sm-2 order-3 w-mob-65">
                <input
                  type="text"
                  value={search}
                  className="form-control form-control-lg"
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search"
                />
                <span className="cursor-pointer search-icon">
                  {search.length ? (
                    <span onClick={() => setSearch("")}>
                      {svg?.searchClear}
                    </span>
                  ) : (
                    svg?.search
                  )}
                </span>
              </div>
              {USER_PERMISSIONS?.ADD && (
                <button
                  className="btn btn-primary btn-lg mob-btn px-md-4 ms-sm-0 ms-auto order-sm-3 order-4"
                  onClick={() => {
                    setSelectedProductTag(null);
                    setIsCreateModalOpen(true);
                  }}
                >
                  + CREATE
                </button>
              )}
              <div className="d-flex gap-md-3 gap-2 order-md-4 order-sm-3 order-2">
                {USER_PERMISSIONS?.MODIFY && (
                  productTags.length > 0 && (
                    <Dropdown
                      overlay={headerMoreMenu}
                      trigger={["click"]}
                      placement="bottomRight"
                    >
                      <span className="cursor-pointer svg-46">
                        {svg?.tableMoreOption}
                      </span>
                    </Dropdown>
                  )
                )}
                <NotificationButton />
              </div>
            </div>
          )}
        </div>
        <div className="card page-table-card page-table-device border-0 shadow rounded-4 flex-fill overflow-y-auto" style={{ minHeight: 0 }}>
          <div className="card-body overflow-y-auto p-0" id="scrollableDiv">
            {loading ? (
              <FullScreenLoader />
            ) : productTags?.length === 0 ? (
              <NoDataMessage
                icon={svg?.noProductTags}
                text="You do not have any Product Tags yet."
              />
            ) : (
              <InfiniteScroll
                dataLength={productTags.length}
                next={handleLoadMore}
                hasMore={hasMore}
                loader={
                  <div className="text-center p-2">
                    <Spin />
                  </div>
                }
                scrollableTarget="scrollableDiv"
                height={"100%"}
              >
                {isReorderMode ? (
                  <DndContext
                    sensors={sensors}
                    modifiers={[restrictToVerticalAxis]}
                    onDragEnd={onDragEnd}
                  >
                    <SortableContext
                      items={productTags.map((i) => i._id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <Table
                        dataSource={productTags}
                        scroll={{ x: "max-content" }}
                        pagination={false}
                        columns={columns}
                        onChange={handleTableChange}
                        rowKey={(record) => record._id}
                        className="custom-table"
                        bordered
                        components={tableComponents}
                      />
                    </SortableContext>
                  </DndContext>
                ) : !isMobile ? (
                  <Table
                    dataSource={productTags}
                    scroll={{ x: "max-content" }}
                    pagination={false}
                    columns={columns}
                    onChange={handleTableChange}
                    rowKey={(record) => record._id}
                    className="custom-table"
                    bordered
                    components={tableComponents}
                  />
                ) : (
                  <div className="px-3">
                    <div className="row gx-3 gy-3">
                      {productTags?.map((record: any, index: number) => {
                        const createdByName = getDecryptedName(record.created_by) || "-";
                        const createdOn = formatToLocalDate(record.created_at, true) || "-";

                        return (
                          <div className="col-md-6" key={record._id || index}>
                            <div className="card mobile-table-card mobile-font-s rounded-4 p-3 h-100">
                              {/* Header: Tag Badge & Action Dropdown */}
                              <div className="row align-items-start mb-2">
                                <div className="col-sm-5 col-5 text-secondary fw-medium">
                                  Name:
                                </div>
                                <div className="col-sm-7 col-7 d-flex justify-content-between gap-2">
                                  <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
                                    <span
                                      style={{
                                        backgroundColor: record?.color ?? "#000000",
                                        minWidth: 0,
                                        wordBreak: "break-word",
                                        overflowWrap: "anywhere",
                                        whiteSpace: "normal",
                                      }}
                                      className="px-2 py-1 rounded-3 text-white fw-bold fs-6"
                                    >
                                      {record.name || "-"}
                                    </span>
                                  </div>

                                  {(USER_PERMISSIONS?.MODIFY || (USER_PERMISSIONS?.REMOVE && !record?.is_default)) && (
                                    <div className="flex-shrink-0 ms-2" onClick={(e) => e.stopPropagation()}>
                                      <Dropdown
                                        overlay={() => MoreOptionsMenu(record)}
                                        trigger={["click"]}
                                        placement="bottomRight"
                                        overlayClassName="moreOptionDropdown"
                                      >
                                        <span className="cursor-pointer d-inline-block">
                                          {svg?.tableMobileOption}
                                        </span>
                                      </Dropdown>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Details Grid */}
                              <div className="mobile-card-details fs-6 fs-6-mob">
                                <div className="row gy-2">
                                  <div className="col-12">
                                    <div className="row">
                                      <div className="col-sm-5 col-5 text-secondary fw-medium">
                                        Created By:
                                      </div>
                                      <div className="col-sm-7 col-7">
                                        {createdByName}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="col-12">
                                    <div className="row">
                                      <div className="col-sm-5 col-5 text-secondary fw-medium">
                                        Created On:
                                      </div>
                                      <div className="col-sm-7 col-7">
                                        {createdOn}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </InfiniteScroll>
            )}
          </div>
        </div>
      </div>

      {isCreateModalOpen && (
        <CreateProductTagModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          refreshData={() => {
            setCurrentPage(1);
            fetchProductTags(1, true);
          }}
          editData={selectedProductTag}
        />
      )}

      <CommonConfirmationModal
        open={idToDelete != null}
        title="Remove Product Tag, Are You Sure?"
        subTitle="Removing this product tag will delete it from the list and it will no longer be available for selection. Please confirm before proceeding."
        loading={deleting}
        onSuccess={handleDeleteConfirm}
        onClose={handleCloseDeleteModal}
      />
    </>
  ) : authLoading ? (
    <FullScreenLoader />
  ) : (
    <NoPermissionPage />
  );
};

export default ProductTags;
